import React, { useState, useEffect } from 'react';
import { billSettingsAPI } from '../../services/api';

const BillSettings = () => {
  const [form, setForm]       = useState({ prefix: '', suffix: '', next_number: 1 });
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    billSettingsAPI.get()
      .then((res) => {
        if (res.success) {
          setForm({
            prefix:      res.data.prefix,
            suffix:      res.data.suffix,
            next_number: res.data.next_number,
          });
          setPreview(res.data.preview);
        }
      })
      .catch(() => setMessage({ type: 'error', text: 'Failed to load bill settings.' }))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...form, [name]: name === 'next_number' ? parseInt(value, 10) || 1 : value };
    setForm(updated);
    setPreview(`${updated.prefix}${updated.next_number}${updated.suffix}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const res = await billSettingsAPI.update(form);
      if (res.success) {
        setPreview(res.data.preview);
        setMessage({ type: 'success', text: 'Bill settings saved successfully.' });
      }
    } catch (err) {
      const errors = err.response?.data?.errors;
      const text   = errors
        ? Object.values(errors)[0][0]
        : err.response?.data?.message || 'Failed to save.';
      setMessage({ type: 'error', text });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: '2rem' }}>Loading...</div>;

  return (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: '2rem' }}>
      <h2 style={{ marginBottom: 4 }}>Bill Number Settings</h2>
      <p style={{ color: '#6b7280', fontSize: 14, marginBottom: '1.5rem' }}>
        Configure the prefix, suffix, and starting number for auto-generated invoice numbers.
      </p>

      {message && (
        <div style={{
          padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '1.5rem', fontSize: 14,
          background: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
          color:      message.type === 'success' ? '#166534' : '#991b1b',
          border:     `1px solid ${message.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
        }}>
          {message.text}
        </div>
      )}

      {/* Live preview */}
      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1.5rem', textAlign: 'center' }}>
        <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>Next invoice number preview</p>
        <p style={{ fontSize: 26, fontWeight: 700, color: '#630137', letterSpacing: 1 }}>
          {preview || '—'}
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={labelStyle}>Prefix</label>
            <input
              type="text"
              name="prefix"
              value={form.prefix}
              onChange={handleChange}
              maxLength={20}
              style={inputStyle}
              placeholder="e.g. INV- or APT/"
            />
          </div>
          <div>
            <label style={labelStyle}>Suffix</label>
            <input
              type="text"
              name="suffix"
              value={form.suffix}
              onChange={handleChange}
              maxLength={20}
              style={inputStyle}
              placeholder="e.g. /24-25"
            />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Next Number *</label>
          <input
            type="number"
            name="next_number"
            value={form.next_number}
            onChange={handleChange}
            min={1}
            required
            style={inputStyle}
          />
          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
            This auto-increments after each invoice is created.
          </p>
        </div>

        <button
          type="submit"
          disabled={saving}
          style={{
            padding: '0.75rem 1.5rem', background: saving ? '#aaa' : '#630137',
            color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600,
            fontSize: 15, cursor: saving ? 'not-allowed' : 'pointer', alignSelf: 'flex-start',
          }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </form>
    </div>
  );
};

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
const inputStyle  = { width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };

export default BillSettings;
